import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQueryErrorResetBoundary, useSuspenseQuery} from '@github-ui/react-query'
import {InfoIcon} from '@primer/octicons-react'
import {useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {TemplateListPaneQuery} from './__generated__/TemplateListPaneQuery.graphql'
import {LABELS} from './constants/labels'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {fetchTemplateList, templateListQueryKey} from './fetch-template-list'
import type {RepositoryNode} from './template-list-query-types'
import {TemplateList, type TemplateListSelectedProp, type TemplateListSharedProps} from './TemplateList'
import styles from './TemplateListPane.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const TemplateListPaneGraphQLQuery = graphql`
  query TemplateListPaneQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        hasIssuesEnabled
        id
        nameWithOwner
        issueForms {
          __typename
          filename
          name
          description
        }
        issueTemplates {
          __typename
          filename
          name
          about
        }
        isBlankIssuesEnabled
        isBlankIssuesMaintainerOverride
        isSecurityPolicyEnabled
        securityPolicyUrl
        contactLinks {
          name
          about
          url
        }
        hasAnyTemplates
      }
    }
  }
`

export type TemplateListPaneFragment = Pick<
  RepositoryNode,
  | 'hasIssuesEnabled'
  | 'id'
  | 'nameWithOwner'
  | 'issueForms'
  | 'issueTemplates'
  | 'isBlankIssuesEnabled'
  | 'isBlankIssuesMaintainerOverride'
  | 'isSecurityPolicyEnabled'
  | 'securityPolicyUrl'
  | 'contactLinks'
  | 'hasAnyTemplates'
>

export type TemplateListPaneInternalProps = {
  repository: TemplateListPaneFragment
} & TemplateListPaneSharedProps

type TemplateListPaneSharedProps = {
  /** Id to use on validation messages
   *
   * `TemplateListNewPane` might be used with a repository that has issues disabled or has no templates available.
   * When this is the case, a message is shown and rendered with the id passed in this property.
   *
   * Consumers can then use this id in conjuction with an `aria-describedby` to ensure their control is properly
   * described with the message and accessible.
   */
  descriptionId?: string
} & TemplateListSelectedProp &
  TemplateListSharedProps

type TemplateListPaneFromRepoProps = {
  repositoryId: string
  owner: string
  repo: string
} & TemplateListPaneSharedProps

export function TemplateListPaneFromRepo({repositoryId, owner, repo, ...props}: TemplateListPaneFromRepoProps) {
  if (!isFeatureEnabled('issues_new_react_data_router')) {
    return <TemplateListPaneFromRepoRelay repositoryId={repositoryId} {...props} />
  }

  return <TemplateListPaneFromRepoServerPersisted owner={owner} repo={repo} {...props} />
}

function TemplateListPaneFromRepoRelay({repositoryId, ...props}: {repositoryId: string} & TemplateListPaneSharedProps) {
  const data = useLazyLoadQuery<TemplateListPaneQuery>(TemplateListPaneGraphQLQuery, {
    id: repositoryId,
  })
  if (!data.node) return null

  return <TemplateListInternal repository={data.node as TemplateListPaneFragment} {...props} />
}

function TemplateListPaneFromRepoServerPersisted({
  owner,
  repo,
  ...props
}: {owner: string; repo: string} & TemplateListPaneSharedProps) {
  const {reset} = useQueryErrorResetBoundary()
  const [retryKey, setRetryKey] = useState(0)

  const retry = () => {
    reset()
    setRetryKey(key => key + 1)
  }

  return (
    <ErrorBoundary key={retryKey} boundaryName="TemplateListPane" fallback={<TemplateListError onRetry={retry} />}>
      <TemplateListPaneFromRepoInternal owner={owner} repo={repo} {...props} />
    </ErrorBoundary>
  )
}

function TemplateListPaneFromRepoInternal({
  owner,
  repo,
  ...props
}: {owner: string; repo: string} & TemplateListPaneSharedProps) {
  const {data: repository} = useSuspenseQuery({
    queryKey: templateListQueryKey(owner, repo),
    queryFn: ({signal}) => fetchTemplateList(owner, repo, signal),
  })

  return <TemplateListInternal repository={repository} {...props} />
}

export function TemplateListInternal({repository, descriptionId, ...props}: TemplateListPaneInternalProps) {
  const {optionConfig} = useIssueCreateConfigContext()
  const {insidePortal} = optionConfig

  return (
    <div>
      {repository.hasIssuesEnabled ? (
        <TemplateList
          repository={repository}
          {...props}
          className={`${
            !insidePortal
              ? `border borderColor-muted rounded-2 overflow-hidden
                ${optionConfig.showRepositoryPicker ? 'pt-0 mt-0' : 'py-2 mt-2'}`
              : ``
          }`}
        />
      ) : (
        <div className={`${styles.disabledStateGap} d-flex flex-row flex-items-center tmp-m-3`}>
          <InfoIcon />
          <span id={descriptionId}>{LABELS.issuesDisabledForRepo}</span>
        </div>
      )}
    </div>
  )
}

function TemplateListError({onRetry}: {onRetry: () => void}) {
  return (
    <div className="d-flex flex-row flex-items-center tmp-m-3 color-fg-danger">
      <span>Failed to load templates.</span>
      <button type="button" className="btn-link ml-2" onClick={onRetry}>
        Retry
      </button>
    </div>
  )
}
